import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  Button,
  Divider,
  Alert,
  FormControlLabel,
  Tab,
  Tabs
} from '@mui/material';
import {
  Security,
  Notifications,
  DataUsage,
  Language,
  Storage,
  Save,
  CloudUpload,
  CloudDownload,
  SettingsBackupRestore
} from '@mui/icons-material';

// Mock settings data
const initialSettings = {
  security: {
    twoFactorAuth: true,
    passwordExpiration: 90,
    loginAttempts: 5,
    sessionTimeout: 30
  },
  notifications: {
    emailNotifications: true,
    pushNotifications: true,
    marketAlerts: true,
    systemAlerts: true
  },
  data: {
    autoBackup: true,
    backupFrequency: 'daily',
    retentionPeriod: 30,
    storageLimit: 10
  },
  localization: {
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    language: 'en-US',
    currency: 'USD'
  },
  system: {
    maintenanceMode: false,
    debugMode: false,
    analyticsEnabled: true,
    apiRateLimit: 100
  }
};

function AdminSettings() {
  const [settings, setSettings] = useState(initialSettings);
  const [activeTab, setActiveTab] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (section, setting, value) => {
    setSettings({
      ...settings,
      [section]: {
        ...settings[section],
        [setting]: value
      }
    });
  };

  const handleToggleChange = (section, setting) => {
    setSettings({
      ...settings,
      [section]: {
        ...settings[section],
        [setting]: !settings[section][setting]
      }
    });
  };

  const handleSaveSettings = () => {
    // In a real app, this would save to a backend
    setSuccessMessage('Settings saved successfully');
    
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const handleResetSettings = () => {
    setSettings(initialSettings);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>System Settings</Typography>
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant="scrollable" 
          scrollButtons="auto"
        >
          <Tab label="Security" icon={<Security />} iconPosition="start" />
          <Tab label="Notifications" icon={<Notifications />} iconPosition="start" />
          <Tab label="Data Management" icon={<DataUsage />} iconPosition="start" />
          <Tab label="Localization" icon={<Language />} iconPosition="start" />
          <Tab label="System" icon={<Storage />} iconPosition="start" />
        </Tabs>
      </Box>
      
      {/* Security Settings */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Authentication</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.security.twoFactorAuth}
                      onChange={() => handleToggleChange('security', 'twoFactorAuth')}
                    />
                  }
                  label="Two-Factor Authentication"
                />
                <Typography variant="body2" color="text.secondary">
                  Require two-factor authentication for all admin accounts
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Password Expiration (days)"
                  type="number"
                  value={settings.security.passwordExpiration}
                  onChange={(e) => handleInputChange('security', 'passwordExpiration', parseInt(e.target.value))}
                  InputProps={{ inputProps: { min: 0 } }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Number of days before passwords expire (0 = never)
                </Typography>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Access Control</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Failed Login Attempts"
                  type="number"
                  value={settings.security.loginAttempts}
                  onChange={(e) => handleInputChange('security', 'loginAttempts', parseInt(e.target.value))}
                  InputProps={{ inputProps: { min: 1 } }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Number of failed attempts before account lockout
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Session Timeout (minutes)"
                  type="number"
                  value={settings.security.sessionTimeout}
                  onChange={(e) => handleInputChange('security', 'sessionTimeout', parseInt(e.target.value))}
                  InputProps={{ inputProps: { min: 1 } }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Minutes of inactivity before auto-logout
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {/* Notification Settings */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Notification Channels</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <List>
                <ListItem>
                  <ListItemText 
                    primary="Email Notifications" 
                    secondary="Send system notifications via email"
                  />
                  <Switch 
                    checked={settings.notifications.emailNotifications}
                    onChange={() => handleToggleChange('notifications', 'emailNotifications')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Push Notifications" 
                    secondary="Send real-time notifications to browser/mobile"
                  />
                  <Switch 
                    checked={settings.notifications.pushNotifications}
                    onChange={() => handleToggleChange('notifications', 'pushNotifications')}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Notification Types</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <List>
                <ListItem>
                  <ListItemText 
                    primary="Market Alerts" 
                    secondary="Notifications about market events and price changes"
                  />
                  <Switch 
                    checked={settings.notifications.marketAlerts}
                    onChange={() => handleToggleChange('notifications', 'marketAlerts')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="System Alerts" 
                    secondary="Notifications about system events and status"
                  />
                  <Switch 
                    checked={settings.notifications.systemAlerts}
                    onChange={() => handleToggleChange('notifications', 'systemAlerts')}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {/* Data Management Settings */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Backup Settings</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.data.autoBackup}
                      onChange={() => handleToggleChange('data', 'autoBackup')}
                    />
                  }
                  label="Automatic Backups"
                />
                <Typography variant="body2" color="text.secondary">
                  Automatically backup system data
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  select
                  fullWidth
                  label="Backup Frequency"
                  value={settings.data.backupFrequency}
                  onChange={(e) => handleInputChange('data', 'backupFrequency', e.target.value)}
                  SelectProps={{ native: true }}
                  disabled={!settings.data.autoBackup}
                >
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </TextField>
              </Box>
              
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  startIcon={<CloudDownload />}
                >
                  Download Backup
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<CloudUpload />}
                >
                  Upload Backup
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Storage Settings</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Retention Period (days)"
                  type="number"
                  value={settings.data.retentionPeriod}
                  onChange={(e) => handleInputChange('data', 'retentionPeriod', parseInt(e.target.value))}
                  InputProps={{ inputProps: { min: 1 } }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Number of days to keep backups
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Storage Limit (GB)"
                  type="number"
                  value={settings.data.storageLimit}
                  onChange={(e) => handleInputChange('data', 'storageLimit', parseInt(e.target.value))}
                  InputProps={{ inputProps: { min: 1 } }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Maximum storage space for backups
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {/* Localization Settings */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Regional Settings</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  select
                  fullWidth
                  label="Timezone"
                  value={settings.localization.timezone}
                  onChange={(e) => handleInputChange('localization', 'timezone', e.target.value)}
                  SelectProps={{ native: true }}
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="Europe/London">London (GMT)</option>
                  <option value="Europe/Paris">Central European Time (CET)</option>
                  <option value="Asia/Tokyo">Japan (JST)</option>
                </TextField>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  select
                  fullWidth
                  label="Default Language"
                  value={settings.localization.language}
                  onChange={(e) => handleInputChange('localization', 'language', e.target.value)}
                  SelectProps={{ native: true }}
                >
                  <option value="en-US">English (US)</option>
                  <option value="en-GB">English (UK)</option>
                  <option value="es-ES">Spanish</option>
                  <option value="fr-FR">French</option>
                  <option value="de-DE">German</option>
                  <option value="ja-JP">Japanese</option>
                  <option value="zh-CN">Chinese (Simplified)</option>
                </TextField>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Format Settings</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  select
                  fullWidth
                  label="Date Format"
                  value={settings.localization.dateFormat}
                  onChange={(e) => handleInputChange('localization', 'dateFormat', e.target.value)}
                  SelectProps={{ native: true }}
                >
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </TextField>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  select
                  fullWidth
                  label="Currency"
                  value={settings.localization.currency}
                  onChange={(e) => handleInputChange('localization', 'currency', e.target.value)}
                  SelectProps={{ native: true }}
                >
                  <option value="USD">US Dollar ($)</option>
                  <option value="EUR">Euro (€)</option>
                  <option value="GBP">British Pound (£)</option>
                  <option value="JPY">Japanese Yen (¥)</option>
                  <option value="AUD">Australian Dollar</option>
                  <option value="CAD">Canadian Dollar</option>
                </TextField>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {/* System Settings */}
      {activeTab === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>System Mode</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.system.maintenanceMode}
                      onChange={() => handleToggleChange('system', 'maintenanceMode')}
                    />
                  }
                  label="Maintenance Mode"
                />
                <Typography variant="body2" color="text.secondary">
                  Enable maintenance mode (will make site unavailable to regular users)
                </Typography>
                {settings.system.maintenanceMode && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Maintenance mode is currently active. Only administrators can access the site.
                  </Alert>
                )}
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.system.debugMode}
                      onChange={() => handleToggleChange('system', 'debugMode')}
                    />
                  }
                  label="Debug Mode"
                />
                <Typography variant="body2" color="text.secondary">
                  Enable debug mode for detailed error reporting
                </Typography>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Performance</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={settings.system.analyticsEnabled}
                      onChange={() => handleToggleChange('system', 'analyticsEnabled')}
                    />
                  }
                  label="Enable Analytics"
                />
                <Typography variant="body2" color="text.secondary">
                  Collect anonymized usage data to improve system performance
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="API Rate Limit (requests/minute)"
                  type="number"
                  value={settings.system.apiRateLimit}
                  onChange={(e) => handleInputChange('system', 'apiRateLimit', parseInt(e.target.value))}
                  InputProps={{ inputProps: { min: 10 } }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Maximum API requests per minute per user
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {/* Action Buttons */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button 
          variant="outlined" 
          startIcon={<SettingsBackupRestore />}
          onClick={handleResetSettings}
          sx={{ mr: 2 }}
        >
          Reset Settings
        </Button>
        <Button 
          variant="contained" 
          startIcon={<Save />}
          onClick={handleSaveSettings}
        >
          Save Settings
        </Button>
      </Box>
    </Box>
  );
}

export default AdminSettings;
